import { Navigate } from 'react-router-dom';
import { hasRole } from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import { useOrgMemberStore } from '@/store/orgMember';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useOrgPaths } from '@/hooks/useOrgPaths';

interface LessonsPortalGuardProps {
  children: React.ReactNode;
}

/**
 * Redirects club-only members away from lesson portal routes.
 */
export function LessonsPortalGuard({ children }: LessonsPortalGuardProps) {
  const { user } = useAuthStore();
  const orgProfile = useOrgMemberStore((s) => s.orgProfile);
  const orgMemberAcademy = useOrgMemberStore((s) => s.academy);
  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);
  const { useUnifiedNav, hasLessonsAccess } = useUnifiedConsumerNav();
  const { p } = useOrgPaths();

  const userHasLessonsRole = Boolean(
    user && (hasRole(user, 'guardian') || hasRole(user, 'student'))
  );
  const academyPending =
    useUnifiedNav &&
    userHasLessonsRole &&
    orgProfile?.modules.hasAcademy &&
    Boolean(orgProfile.academySlug) &&
    !guardianAcademy &&
    !orgMemberAcademy &&
    !orgProfile.academy;

  if (academyPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (useUnifiedNav && !hasLessonsAccess) {
    const home = p('') ?? p('/dashboard');
    return <Navigate to={home ?? '/org-picker'} replace />;
  }

  return <>{children}</>;
}
